/**
 * ShipModificationDialog — Equip/unequip components at a shipyard station.
 *
 * Layout: ship image + total stats (top), 8 category slots + detail + available list (bottom).
 * Opened via EventBus 'open_ship_modification' with { unitId, stationId }.
 */

import Phaser from 'phaser';
import { ServiceLocator, Services } from '../../utils/ServiceLocator';
import { MMOEvents } from '../../managers/MMONetworkManager';
import type { MMOStateManager } from '../../managers/MMOStateManager';
import { createDialogChrome, closeDialog, Fonts, FontSize } from './GameUITheme';
import { EventBus } from '../../utils/EventBus';
import { isMobile } from '../../utils/ResponsiveHelper';
import { I18n } from '../../i18n/I18nService';
import { SHIP_USD_PRICES } from '../../shared/common/MonetizationTypes';
const PAD = 12;

const CATEGORIES = [
  { id: 'weapon', label: 'WPN', color: 0xff4444 },
  { id: 'engine', label: 'ENG', color: 0x44aaff },
  { id: 'shield', label: 'SHD', color: 0x44ddff },
  { id: 'armor', label: 'ARM', color: 0x888888 },
  { id: 'sensor', label: 'SNS', color: 0xffcc44 },
  { id: 'stealth', label: 'STL', color: 0x9944ff },
  { id: 'reactor', label: 'RCT', color: 0xff8800 },
  { id: 'special', label: 'SPC', color: 0x44ff88 },
] as const;

type CategoryId = typeof CATEGORIES[number]['id'];

const TIER_LABELS: Record<string, string> = {
  mk0: 'Mk 0', mk1: 'Mk I', mk2: 'Mk II', mk3: 'Mk III', mk4: 'Mk IV', prototype: 'Proto',
};

const RARITY_COLORS: Record<string, string> = {
  common: '#aaaaaa',
  uncommon: '#44cc44',
  rare: '#4488ff',
  epic: '#aa44ff',
  legendary: '#ffaa00',
};

const FEE_BY_TIER: Record<string, number> = {
  mk0: 0, mk1: 5, mk2: 15, mk3: 50, mk4: 150, prototype: 500,
};

const STAT_LABELS: Record<string, string> = {
  hp: 'Hull HP', shields: 'Shields', armor: 'Armor', speed: 'Speed',
  evasion: 'Evasion', range: 'Sensor Range', accuracy: 'Accuracy',
  attack: 'Attack', defense: 'Defense', power: 'Power Output',
  cooldownReduction: 'Cooldown Reduc.', regenRate: 'Shield Regen',
  damage: 'Damage', fireRate: 'Fire Rate', speedBonus: 'Speed Bonus',
  miningYieldBonus: 'Mining Yield', maxMineralRarity: 'Max Mineral Rarity',
  weight: 'Weight',
};

interface ComponentInfo {
  id: string;
  definitionId: string;
  name: string;
  category: string;
  currentTier: string;
  tier: string;
  rarity: string;
  starRating: number;
  conditionPercent: number;
  stats: Record<string, number | string>;
  compatibleShipTypes: string[];
  description: string;
}

const WEAPON_CLASS_LABELS: Record<string, string> = {
  light: 'LIGHT',
  medium: 'MEDIUM',
  heavy: 'HEAVY',
  artillery: 'ARTILLERY',
  super_heavy: 'SUPER HEAVY',
};

const WEAPON_CLASS_COLORS: Record<string, string> = {
  light: '#88ccaa',
  medium: '#44aaff',
  heavy: '#ff8844',
  artillery: '#ff4488',
  super_heavy: '#ff44ff',
};

export class ShipModificationDialog extends Phaser.GameObjects.Container {
  private dw: number;
  private dh: number;
  private mmoState: MMOStateManager;

  private unitId: string;
  private stationId: string;

  // Shipyard info
  private shipyardLevel = 1;
  private levelName = 'Basic';
  private feePct = 0;

  // Component data
  private equippedComponents: ComponentInfo[] = [];
  private locationComponents: ComponentInfo[] = [];

  // UI state
  private selectedCategory: CategoryId = 'weapon';
  private shipyardLoaded = false;
  private shipComponentsLoaded = false;
  private locationComponentsLoaded = false;
  private pendingModComponentId: string | null = null;

  // UI containers
  private shipContainer!: Phaser.GameObjects.Container;
  private statsContainer!: Phaser.GameObjects.Container;
  private slotsContainer!: Phaser.GameObjects.Container;
  private detailContainer!: Phaser.GameObjects.Container;
  private listContainer!: Phaser.GameObjects.Container;
  private statusText!: Phaser.GameObjects.Text;
  private balanceText!: Phaser.GameObjects.Text;

  // Event handlers for cleanup
  private onShipyardInfo: ((data: any) => void) | null = null;
  private onShipComponents: ((data: any) => void) | null = null;
  private onLocationComponents: ((data: any) => void) | null = null;
  private onModResult: ((data: any) => void) | null = null;

  constructor(scene: Phaser.Scene, unitId: string, stationId: string) {
    const mobile = isMobile(scene);
    const dw = mobile ? 340 : 820;
    const dh = mobile ? 540 : 700;
    const { width, height } = scene.cameras.main;
    super(scene, width / 2 - dw / 2, height / 2 - dh / 2);
    this.dw = dw;
    this.dh = dh;

    this.unitId = unitId;
    this.stationId = stationId;
    this.mmoState = ServiceLocator.get<MMOStateManager>(Services.MMO_STATE);

    createDialogChrome(scene, this, this.dw, this.dh, {
      title: I18n.t('dialog.ship_modification'),
      onClose: () => this.close(),
    });

    // Ship image area (left side of top section)
    this.shipContainer = scene.add.container(PAD, 50);
    this.add(this.shipContainer);

    // Total stats panel (right of ship image)
    this.statsContainer = scene.add.container(280, 50);
    this.add(this.statsContainer);

    // Bottom section
    const BOTTOM_Y = 360;

    // Slots area (left side, below ship)
    this.slotsContainer = scene.add.container(PAD, BOTTOM_Y);
    this.add(this.slotsContainer);

    // Detail panel (right of slots)
    this.detailContainer = scene.add.container(230, BOTTOM_Y);
    this.add(this.detailContainer);

    // Available components list
    this.listContainer = scene.add.container(230, BOTTOM_Y + 110);
    this.add(this.listContainer);

    // Status bar
    this.statusText = scene.add.text(this.dw / 2, this.dh - 18, '', {
      fontSize: FontSize.sm,
      color: '#889999',
      fontFamily: Fonts.body,
    });
    this.statusText.setOrigin(0.5);
    this.add(this.statusText);

    this.setDepth(2000);
    scene.add.existing(this);

    this.registerEventHandlers();
    this.requestData();
  }

  private sendWs(msg: { type: string; payload: any }): void {
    EventBus.emit('send_message', msg);
  }

  private registerEventHandlers(): void {
    this.onShipyardInfo = (data: any) => {
      if (data?.success) {
        this.shipyardLevel = data.shipyardLevel || 1;
        this.levelName = data.levelName || 'Basic';
        this.feePct = data.feePct || 0;
        this.shipyardLoaded = true;
        this.renderAll();
        this.clearLoadingIfReady();
      } else {
        this.setStatus(data?.error || 'Failed to load shipyard info');
      }
    };

    this.onShipComponents = (data: any) => {
      if (data?.components) {
        this.shipComponentsLoaded = true;
        this.equippedComponents = data.components;
        this.pendingModComponentId = null;
        this.renderAll();
        this.clearLoadingIfReady();
      }
    };

    this.onLocationComponents = (data: any) => {
      if (data?.components) {
        this.locationComponentsLoaded = true;
        this.locationComponents = data.components;
        this.pendingModComponentId = null;
        this.renderAvailableList();
        this.clearLoadingIfReady();
      }
    };

    this.onModResult = (data: any) => {
      if (data?.success) {
        const feeStr = data.fee > 0 ? ` (${data.fee} ISD)` : '';
        this.setStatus(`${data.action === 'equip' ? 'Equipped' : 'Unequipped'} successfully${feeStr}`);
        this.requestComponentData();
        // Refresh ISD balance display
        if (data.newBalance != null) {
          this.mmoState.setResources(data.newBalance);
        }
        if (this.balanceText) {
          const balance = this.mmoState.getResources();
          this.balanceText.setText(`ISD: ${balance.toLocaleString()}  |  Fee: ${this.feePct}%`);
        }
      } else {
        this.pendingModComponentId = null;
        this.setStatus(data?.error || 'Modification failed');
        this.renderAll();
      }
    };

    EventBus.on(MMOEvents.SHIPYARD_INFO, this.onShipyardInfo);
    EventBus.on(MMOEvents.SHIP_COMPONENTS, this.onShipComponents);
    EventBus.on(MMOEvents.LOCATION_COMPONENTS, this.onLocationComponents);
    EventBus.on(MMOEvents.SHIP_MOD_RESULT, this.onModResult);
  }

  private requestData(): void {
    this.setStatus('Loading...');
    this.sendWs({ type: 'mmo_get_shipyard_info', payload: { stationId: this.stationId } });
    this.requestComponentData();
  }

  private getLocationType(): string {
    return this.stationId.startsWith('planet_') ? 'planet' : 'station';
  }

  private requestComponentData(): void {
    this.sendWs({ type: 'mmo_get_ship_components', payload: { unitId: this.unitId } });
    this.sendWs({ type: 'mmo_get_location_components', payload: { locationType: this.getLocationType(), locationId: this.stationId } });
  }

  private clearLoadingIfReady(): void {
    if (this.shipyardLoaded && this.shipComponentsLoaded && this.locationComponentsLoaded) {
      this.setStatus('');
    }
  }

  private renderAll(): void {
    this.renderShip();
    this.renderTotalStats();
    this.renderSlots();
    this.renderDetail();
    this.renderAvailableList();
  }

  /** Aggregate all equipped component stats into a single totals object */
  private getTotalStats(): Record<string, number> {
    const totals: Record<string, number> = {};
    for (const comp of this.equippedComponents) {
      if (!comp.stats) continue;
      for (const [key, val] of Object.entries(comp.stats)) {
        if (typeof val === 'number') {
          totals[key] = (totals[key] || 0) + val;
        }
      }
    }
    return totals;
  }

  private formatStatLabel(key: string): string {
    return STAT_LABELS[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();
  }

  private formatStatValue(key: string, val: number): string {
    if (key === 'maxMineralRarity') {
      const names = ['', 'Common', 'Uncommon', 'Rare', 'Epic', 'Legendary'];
      return names[val] || String(val);
    }
    if (Number.isInteger(val)) return String(val);
    return val.toFixed(2);
  }

  private getSpriteKey(unitType: string): string | null {
    const type = unitType.toLowerCase();
    if (type.includes('leviathan') || type.includes('alien_leviathan')) return 'alien-leviathan';
    if (type.includes('dreadnaught') || type.includes('dreadnought')) return 'dreadnaught';
    if (type.includes('titan')) return 'titan';
    if (type.includes('carrier')) return 'carrier';
    if (type.includes('battleship')) return 'battleship';
    if (type.includes('destroyer')) return 'destroyer';
    if (type.includes('bomber')) return 'bomber';
    if (type.includes('cruiser')) return 'cruiser';
    if (type.includes('fighter')) return 'fighter';
    if (type.includes('scout')) return 'scout';
    if (type.includes('tug')) return 'tug';
    if (type.includes('trade')) return 'tradeship';
    return null;
  }

  private renderShip(): void {
    this.shipContainer.removeAll(true);

    const unit = this.mmoState.getUnit(this.unitId);
    const shipType = unit?.type || 'scout';
    const customName = (unit as any)?.customName;
    const variantName = (unit as any)?.shipVariant;
    const displayLabel = customName
      ? customName
      : variantName
        ? variantName.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
        : shipType.toUpperCase();
    const texKey = this.getSpriteKey(shipType);

    const IMG_SIZE = 220;
    const cx = IMG_SIZE / 2 + 20;
    const cy = 130;

    if (texKey && this.scene.textures.exists(texKey)) {
      const img = this.scene.add.image(cx, cy, texKey);
      img.setDisplaySize(IMG_SIZE, IMG_SIZE);
      this.shipContainer.add(img);
    } else {
      const gfx = this.scene.add.graphics();
      gfx.fillStyle(0x4488ff, 0.6);
      gfx.beginPath();
      const half = IMG_SIZE / 2;
      gfx.moveTo(cx, cy - half);
      gfx.lineTo(cx + half * 0.6, cy + half);
      gfx.lineTo(cx - half * 0.6, cy + half);
      gfx.closePath();
      gfx.fillPath();
      this.shipContainer.add(gfx);
    }

    // Ship name label
    const nameLabel = this.scene.add.text(cx, cy + IMG_SIZE / 2 + 10, displayLabel.toUpperCase(), {
      fontSize: FontSize.md, color: '#aaccee', fontFamily: Fonts.heading,
    });
    nameLabel.setOrigin(0.5);
    this.shipContainer.add(nameLabel);

    // Shipyard badge
    const badge = this.scene.add.text(cx, 0, `${this.levelName} Shipyard`, {
      fontSize: FontSize.sm, color: '#ffcc44', fontFamily: Fonts.heading,
    });
    badge.setOrigin(0.5);
    this.shipContainer.add(badge);
  }

  /** Render total aggregated stats from all equipped components to the right of the ship */
  private renderTotalStats(): void {
    this.statsContainer.removeAll(true);

    const totals = this.getTotalStats();
    const keys = Object.keys(totals);

    // Get max slots from ship class config
    const unit = this.mmoState.getUnit(this.unitId);
    const shipType = (unit?.type || 'scout').toLowerCase();
    const classConfig = SHIP_USD_PRICES[shipType];
    const maxSlots = classConfig?.componentSlots || 2;

    // Header with slot count always visible
    this.statsContainer.add(this.scene.add.text(0, 0, `TOTAL COMPONENT STATS  (${this.equippedComponents.length}/${maxSlots} slots)`, {
      fontSize: FontSize.sm, color: '#5588aa', fontFamily: Fonts.heading,
    }));

    if (keys.length === 0) {
      // Still show equipped slots section even with no stats
      let y = 22;
      this.statsContainer.add(this.scene.add.text(0, y, 'No components equipped', {
        fontSize: FontSize.xs, color: '#556677', fontFamily: Fonts.body, fontStyle: 'italic',
      }));
      y += 18;
      for (let i = 0; i < maxSlots; i++) {
        this.statsContainer.add(this.scene.add.text(0, y, `${i + 1}.`, {
          fontSize: FontSize.xs, color: '#333344', fontFamily: Fonts.body,
        }));
        this.statsContainer.add(this.scene.add.text(18, y, '[ -- ]  Empty slot', {
          fontSize: FontSize.xs, color: '#333344', fontFamily: Fonts.body, fontStyle: 'italic',
        }));
        y += 16;
      }
      return;
    }

    // Stats panel background
    const panelW = 500;
    const panelH = Math.min(keys.length * 18 + 10, 270);
    const bg = this.scene.add.graphics();
    bg.fillStyle(0x0d1117, 0.7);
    bg.fillRoundedRect(-6, 18, panelW, panelH, 4);
    bg.lineStyle(1, 0x334455);
    bg.strokeRoundedRect(-6, 18, panelW, panelH, 4);
    this.statsContainer.add(bg);

    // Two-column layout for stats
    let y = 24;
    const COL2_X = 250;
    let col = 0;

    for (const key of keys) {
      const val = totals[key];
      const label = this.formatStatLabel(key);
      const valStr = this.formatStatValue(key, val);
      const xOff = col === 0 ? 0 : COL2_X;
      const rowY = y;

      // Label
      this.statsContainer.add(this.scene.add.text(xOff, rowY, label, {
        fontSize: FontSize.xs, color: '#667788', fontFamily: Fonts.body,
      }));

      // Value (right-aligned within column)
      const valText = this.scene.add.text(xOff + 220, rowY, valStr, {
        fontSize: FontSize.xs, color: val > 0 ? '#44ff88' : (val < 0 ? '#ff4444' : '#aaccdd'), fontFamily: Fonts.body,
      });
      valText.setOrigin(1, 0);
      this.statsContainer.add(valText);

      col++;
      if (col >= 2) {
        col = 0;
        y += 18;
      }
    }

    // Equipped component list below stats
    if (col !== 0) y += 18; // finish incomplete row
    y += 8;

    this.statsContainer.add(this.scene.add.text(0, y, `EQUIPPED (${this.equippedComponents.length}/${maxSlots} slots)`, {
      fontSize: FontSize.xs, color: '#5588aa', fontFamily: Fonts.heading,
    }));
    y += 16;

    if (this.equippedComponents.length === 0) {
      this.statsContainer.add(this.scene.add.text(0, y, 'No components equipped', {
        fontSize: FontSize.xs, color: '#556677', fontFamily: Fonts.body, fontStyle: 'italic',
      }));
    } else {
      for (let i = 0; i < this.equippedComponents.length; i++) {
        const comp = this.equippedComponents[i];
        const catLabel = (comp.category || '').toUpperCase().slice(0, 3);
        const tierStr = (comp.tier || 'mk1').toUpperCase();
        const condStr = comp.conditionPercent !== undefined ? ` ${comp.conditionPercent}%` : '';

        // Slot number + category tag (show weapon class for weapons)
        this.statsContainer.add(this.scene.add.text(0, y, `${i + 1}.`, {
          fontSize: FontSize.xs, color: '#556677', fontFamily: Fonts.body,
        }));
        const eqWeaponClass = comp.stats?.weapon_class as string | undefined;
        const tagLabel = (comp.category === 'weapon' && eqWeaponClass && WEAPON_CLASS_LABELS[eqWeaponClass])
          ? WEAPON_CLASS_LABELS[eqWeaponClass]
          : catLabel;
        const tagColor = (comp.category === 'weapon' && eqWeaponClass && WEAPON_CLASS_COLORS[eqWeaponClass])
          ? WEAPON_CLASS_COLORS[eqWeaponClass]
          : '#4488aa';
        this.statsContainer.add(this.scene.add.text(18, y, `[${tagLabel}]`, {
          fontSize: FontSize.xs, color: tagColor, fontFamily: Fonts.body,
        }));
        // Component name
        const nameX = tagLabel.length > 3 ? 90 : 60;
        this.statsContainer.add(this.scene.add.text(nameX, y, `${comp.name} ${tierStr}${condStr}`, {
          fontSize: FontSize.xs, color: '#aaccdd', fontFamily: Fonts.body,
        }));
        // Key stat
        const statStr = comp.stats?.damage ? `DMG: ${comp.stats.damage}` :
                        comp.stats?.speedBonus ? `SPD: +${comp.stats.speedBonus}` :
                        comp.stats?.shieldHp ? `SHD: ${comp.stats.shieldHp}` :
                        comp.stats?.defenseBonus ? `DEF: +${comp.stats.defenseBonus}` : '';
        if (statStr) {
          this.statsContainer.add(this.scene.add.text(340, y, statStr, {
            fontSize: FontSize.xs, color: '#44ff88', fontFamily: Fonts.body,
          }));
        }
        y += 16;
      }

      // Show empty slots
      for (let i = this.equippedComponents.length; i < maxSlots; i++) {
        this.statsContainer.add(this.scene.add.text(0, y, `${i + 1}.`, {
          fontSize: FontSize.xs, color: '#333344', fontFamily: Fonts.body,
        }));
        this.statsContainer.add(this.scene.add.text(18, y, '[ -- ]  Empty slot', {
          fontSize: FontSize.xs, color: '#333344', fontFamily: Fonts.body, fontStyle: 'italic',
        }));
        y += 16;
      }
    }
  }

  private renderSlots(): void {
    this.slotsContainer.removeAll(true);

    // Category slot grid (4x2)
    const SLOT_SIZE = 44;
    const SLOT_GAP = 6;
    const GRID_X = 0;
    const GRID_Y = 0;

    for (let i = 0; i < CATEGORIES.length; i++) {
      const cat = CATEGORIES[i];
      const col = i % 4;
      const row = Math.floor(i / 4);
      const x = GRID_X + col * (SLOT_SIZE + SLOT_GAP);
      const y = GRID_Y + row * (SLOT_SIZE + SLOT_GAP);

      const isSelected = this.selectedCategory === cat.id;
      const equipped = this.equippedComponents.find(c => c.category === cat.id);

      // Slot background
      const bg = this.scene.add.graphics();
      bg.fillStyle(isSelected ? 0x334466 : 0x1a1a2e, 1);
      bg.fillRoundedRect(x, y, SLOT_SIZE, SLOT_SIZE, 4);
      bg.lineStyle(2, isSelected ? 0x4488ff : (equipped ? cat.color : 0x333344));
      bg.strokeRoundedRect(x, y, SLOT_SIZE, SLOT_SIZE, 4);
      this.slotsContainer.add(bg);

      // Category label
      const label = this.scene.add.text(x + SLOT_SIZE / 2, y + SLOT_SIZE / 2, cat.label, {
        fontSize: '10px', color: equipped ? '#ffffff' : '#556677', fontFamily: Fonts.heading,
      });
      label.setOrigin(0.5);
      this.slotsContainer.add(label);

      // Equipped indicator dot
      if (equipped) {
        const dot = this.scene.add.graphics();
        const rarityHex = parseInt((RARITY_COLORS[equipped.rarity] || '#aaaaaa').replace('#', ''), 16);
        dot.fillStyle(rarityHex, 1);
        dot.fillCircle(x + SLOT_SIZE - 6, y + 6, 4);
        this.slotsContainer.add(dot);
      }

      // Hit zone
      const hitZone = this.scene.add.zone(x + SLOT_SIZE / 2, y + SLOT_SIZE / 2, SLOT_SIZE, SLOT_SIZE);
      hitZone.setInteractive({ useHandCursor: true });
      hitZone.on('pointerdown', () => {
        this.selectedCategory = cat.id;
        this.renderSlots();
        this.renderDetail();
        this.renderAvailableList();
      });
      this.slotsContainer.add(hitZone);
    }

    // ISD Balance below slots
    const balance = this.mmoState.getResources();
    this.balanceText = this.scene.add.text(0, GRID_Y + 2 * (SLOT_SIZE + SLOT_GAP) + 8,
      `ISD: ${balance.toLocaleString()}  |  Fee: ${this.feePct}%`, {
        fontSize: FontSize.xs, color: '#667788', fontFamily: Fonts.body,
      });
    this.slotsContainer.add(this.balanceText);
  }

  private renderDetail(): void {
    this.detailContainer.removeAll(true);

    const catInfo = CATEGORIES.find(c => c.id === this.selectedCategory);
    const catLabel = catInfo?.label || this.selectedCategory.toUpperCase();
    let y = 0;

    // Category title
    this.detailContainer.add(this.scene.add.text(0, y, `SELECTED: ${catLabel}`, {
      fontSize: FontSize.sm, color: '#5588aa', fontFamily: Fonts.heading,
    }));
    y += 22;

    // Equipped component info
    const equipped = this.equippedComponents.find(c => c.category === this.selectedCategory);
    if (equipped) {
      const stars = '\u2605'.repeat(equipped.starRating);
      const rarityColor = RARITY_COLORS[equipped.rarity] || '#aaaaaa';

      this.detailContainer.add(this.scene.add.text(0, y, equipped.name, {
        fontSize: FontSize.md, color: '#ffffff', fontFamily: Fonts.heading,
      }));
      y += 20;

      const tierStr = TIER_LABELS[equipped.currentTier || equipped.tier] || equipped.currentTier;
      this.detailContainer.add(this.scene.add.text(0, y, `${tierStr}  [${equipped.rarity}]  ${stars}`, {
        fontSize: FontSize.xs, color: rarityColor, fontFamily: Fonts.body,
      }));
      y += 16;

      // Stats with labels
      if (equipped.stats) {
        for (const [key, val] of Object.entries(equipped.stats)) {
          if (typeof val !== 'number') continue;
          const label = this.formatStatLabel(key);
          const valStr = this.formatStatValue(key, val);
          this.detailContainer.add(this.scene.add.text(0, y, `${label}: ${valStr}`, {
            fontSize: FontSize.xs, color: '#aaccdd', fontFamily: Fonts.body,
          }));
          y += 14;
        }
      }

      // Condition
      if (equipped.conditionPercent < 100) {
        y += 2;
        this.detailContainer.add(this.scene.add.text(0, y, `Condition: ${equipped.conditionPercent}%`, {
          fontSize: FontSize.xs, color: equipped.conditionPercent < 50 ? '#ff4444' : '#ffcc44', fontFamily: Fonts.body,
        }));
        y += 14;
      }
      y += 4;

      // Unequip button
      const fee = FEE_BY_TIER[(equipped.currentTier || equipped.tier || 'mk1').toLowerCase()] ?? 0;
      const isPending = this.pendingModComponentId === equipped.id;
      const btnLabel = isPending ? 'UNEQUIPPING...' : fee > 0 ? `UNEQUIP  ${fee} ISD` : 'UNEQUIP';
      this.detailContainer.add(this.createBtn(0, y, 200, 28, btnLabel, 0x553322, 0xaa6633, () => {
        if (this.pendingModComponentId) {
          this.setStatus('Please wait for the current modification to finish.');
          return;
        }
        this.pendingModComponentId = equipped.id;
        this.renderDetail();
        this.sendWs({
          type: 'mmo_ship_mod_unequip',
          payload: { unitId: this.unitId, componentInstanceId: equipped.id, stationId: this.stationId },
        });
        this.setStatus('Unequipping...');
      }, isPending || this.pendingModComponentId !== null));
    } else {
      this.detailContainer.add(this.scene.add.text(0, y, 'No component equipped', {
        fontSize: FontSize.sm, color: '#556677', fontFamily: Fonts.body,
      }));
    }
  }

  /** Compute stat diff between a candidate component and the currently equipped one in the same category */
  private getStatDiff(comp: ComponentInfo): Record<string, { newVal: number; diff: number }> {
    const equipped = this.equippedComponents.find(c => c.category === comp.category);
    const equippedStats = equipped?.stats || {};
    const result: Record<string, { newVal: number; diff: number }> = {};

    // All stat keys from both components
    const allKeys = new Set([...Object.keys(comp.stats || {}), ...Object.keys(equippedStats)]);
    for (const key of allKeys) {
      const newVal = (comp.stats?.[key] ?? 0) as number;
      const oldVal = (equippedStats[key] ?? 0) as number;
      if (typeof newVal !== 'number' || typeof oldVal !== 'number') continue;
      result[key] = { newVal, diff: newVal - oldVal };
    }
    return result;
  }

  private renderAvailableList(): void {
    this.listContainer.removeAll(true);

    // Title
    const locLabel = this.stationId.startsWith('planet_') ? 'AVAILABLE AT PLANET' : 'AVAILABLE AT STATION';
    this.listContainer.add(this.scene.add.text(0, 0, locLabel, {
      fontSize: FontSize.xs, color: '#5588aa', fontFamily: Fonts.heading,
    }));

    // Filter by selected category and compatible ship type
    const unit = this.mmoState.getUnit(this.unitId);
    const shipType = unit?.type?.toLowerCase() || '';

    const filtered = this.locationComponents.filter(c => {
      if (c.category !== this.selectedCategory) return false;
      if (c.compatibleShipTypes.length > 0) {
        const compatLower = c.compatibleShipTypes.map(t => t.toLowerCase());
        if (!compatLower.includes(shipType)) return false;
      }
      return true;
    });

    if (filtered.length === 0) {
      this.listContainer.add(this.scene.add.text(0, 22, 'No compatible components\nat this station', {
        fontSize: FontSize.xs, color: '#556677', fontFamily: Fonts.body,
      }));
      return;
    }

    const RARITY_LEVELS: Record<string, number> = { common: 1, uncommon: 2, rare: 3, epic: 4, legendary: 4 };

    let y = 22;
    for (const comp of filtered) {
      const rarityLevel = RARITY_LEVELS[comp.rarity] || 1;
      const tooHighRarity = rarityLevel > this.shipyardLevel;
      const rarityColor = tooHighRarity ? '#444444' : (RARITY_COLORS[comp.rarity] || '#aaaaaa');
      const tier = (comp.currentTier || comp.tier || 'mk1').toLowerCase();
      const fee = FEE_BY_TIER[tier] ?? 0;
      const dimColor = tooHighRarity ? '#444444' : '#ccddee';

      // Component name + tier/rarity
      const tierStr = TIER_LABELS[comp.currentTier || comp.tier] || comp.currentTier;
      const nameText = this.scene.add.text(0, y, `${comp.name}  ${tierStr}`, {
        fontSize: FontSize.sm, color: dimColor, fontFamily: Fonts.body,
      });
      this.listContainer.add(nameText);

      // Weapon class badge (e.g., LIGHT, HEAVY, ARTILLERY)
      const weaponClass = comp.stats?.weapon_class as string | undefined;
      if (comp.category === 'weapon' && weaponClass && WEAPON_CLASS_LABELS[weaponClass]) {
        const classLabel = WEAPON_CLASS_LABELS[weaponClass];
        const classColor = WEAPON_CLASS_COLORS[weaponClass] || '#aaaaaa';
        const classX = nameText.width + 8;
        const classBadge = this.scene.add.text(classX, y + 2, classLabel, {
          fontSize: '8px', fontStyle: '900', color: classColor, fontFamily: Fonts.heading, letterSpacing: 1,
        });
        this.listContainer.add(classBadge);
      }

      this.listContainer.add(this.scene.add.text(0, y + 15, comp.rarity, {
        fontSize: FontSize.xs, color: rarityColor, fontFamily: Fonts.body,
      }));

      // Stats with diff
      if (comp.stats && !tooHighRarity) {
        const diffs = this.getStatDiff(comp);
        const statKeys = Object.keys(diffs);
        let statY = y + 15;
        // Show stats inline after rarity, compact
        const statParts: string[] = [];
        const statColors: string[] = [];
        for (const key of statKeys) {
          const { newVal, diff } = diffs[key];
          const label = this.formatStatLabel(key);
          const valStr = this.formatStatValue(key, newVal);
          if (diff === 0) {
            statParts.push(`${label}: ${valStr}`);
            statColors.push('#aaccdd');
          } else {
            const sign = diff > 0 ? '+' : '';
            const diffStr = `${sign}${this.formatStatValue(key, diff)}`;
            statParts.push(`${label}: ${valStr} (${diffStr})`);
            statColors.push(diff > 0 ? '#44ff88' : '#ff4444');
          }
        }

        // Render each stat on its own line below the name
        let sx = 80; // offset from rarity text
        const maxStatX = 400;
        for (let si = 0; si < statParts.length; si++) {
          // If too wide, wrap to next line
          if (sx + statParts[si].length * 5.5 > maxStatX) {
            sx = 0;
            statY += 13;
          }
          const st = this.scene.add.text(sx, statY, statParts[si], {
            fontSize: '10px', color: statColors[si], fontFamily: Fonts.body,
          });
          this.listContainer.add(st);
          sx += st.width + 12;
        }
        y = statY;
      }

      if (tooHighRarity) {
        const reqLevel = ['Basic', 'Advanced', 'Elite', 'Legendary'][rarityLevel - 1] || 'Legendary';
        this.listContainer.add(this.scene.add.text(420, y - 10, `Requires ${reqLevel}`, {
          fontSize: FontSize.xs, color: '#ff4444', fontFamily: Fonts.body,
        }));
      } else {
        // Equip button (right-aligned)
        const isPending = this.pendingModComponentId === comp.id;
        const btnLabel = isPending ? 'EQUIPPING...' : fee > 0 ? `EQUIP ${fee}` : 'EQUIP';
        this.listContainer.add(this.createBtn(460, y - 14, 110, 26, btnLabel, 0x224422, 0x44aa44, () => {
          if (this.pendingModComponentId) {
            this.setStatus('Please wait for the current modification to finish.');
            return;
          }
          this.pendingModComponentId = comp.id;
          this.renderAvailableList();
          this.sendWs({
            type: 'mmo_ship_mod_equip',
            payload: { unitId: this.unitId, componentInstanceId: comp.id, stationId: this.stationId },
          });
          this.setStatus('Equipping...');
        }, isPending || this.pendingModComponentId !== null));
      }

      y += 24;
    }
  }

  private createBtn(
    x: number, y: number, w: number, h: number,
    label: string, bg: number, border: number,
    onClick: () => void,
    disabled = false,
  ): Phaser.GameObjects.Container {
    const container = this.scene.add.container(x, y);

    const gfx = this.scene.add.graphics();
    gfx.fillStyle(disabled ? 0x222833 : bg, 1);
    gfx.fillRoundedRect(0, 0, w, h, 4);
    gfx.lineStyle(1, disabled ? 0x445566 : border);
    gfx.strokeRoundedRect(0, 0, w, h, 4);
    container.add(gfx);

    const text = this.scene.add.text(w / 2, h / 2, label, {
      fontSize: FontSize.xs, color: disabled ? '#8899aa' : '#ffffff', fontFamily: Fonts.heading,
    });
    text.setOrigin(0.5);
    container.add(text);

    const hitZone = this.scene.add.zone(w / 2, h / 2, w, h);
    if (!disabled) {
      hitZone.setInteractive({ useHandCursor: true });
      hitZone.on('pointerdown', onClick);
      hitZone.on('pointerover', () => { gfx.clear(); gfx.fillStyle(border, 0.4); gfx.fillRoundedRect(0, 0, w, h, 4); gfx.lineStyle(1, border); gfx.strokeRoundedRect(0, 0, w, h, 4); });
      hitZone.on('pointerout', () => { gfx.clear(); gfx.fillStyle(bg, 1); gfx.fillRoundedRect(0, 0, w, h, 4); gfx.lineStyle(1, border); gfx.strokeRoundedRect(0, 0, w, h, 4); });
    }
    container.add(hitZone);

    return container;
  }

  private setStatus(msg: string): void {
    this.statusText.setText(msg);
  }

  private close(): void {
    if (this.onShipyardInfo) EventBus.off(MMOEvents.SHIPYARD_INFO, this.onShipyardInfo);
    if (this.onShipComponents) EventBus.off(MMOEvents.SHIP_COMPONENTS, this.onShipComponents);
    if (this.onLocationComponents) EventBus.off(MMOEvents.LOCATION_COMPONENTS, this.onLocationComponents);
    if (this.onModResult) EventBus.off(MMOEvents.SHIP_MOD_RESULT, this.onModResult);
    closeDialog(this.scene, this);
  }
}
